import { supabase } from '../lib/supabase';

export const complianceReportService = {
  // ============================================================================
  // COMPLIANCE REPORTS
  // ============================================================================

  async getAllReports() {
    const { data, error } = await supabase?.from('compliance_reports')?.select('*')?.order('period_start', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      reportName: row?.report_name,
      reportPeriodType: row?.report_period_type,
      periodStart: row?.period_start,
      periodEnd: row?.period_end,
      reportStatus: row?.report_status,
      overallComplianceScore: row?.overall_compliance_score,
      previousPeriodScore: row?.previous_period_score,
      variancePercentage: row?.variance_percentage,
      varianceType: row?.variance_type,
      totalViolations: row?.total_violations,
      criticalViolations: row?.critical_violations,
      resolvedViolations: row?.resolved_violations,
      totalEmployees: row?.total_employees,
      compliantEmployees: row?.compliant_employees,
      reportSummary: row?.report_summary,
      generatedBy: row?.generated_by,
      generatedAt: row?.generated_at,
      reviewedBy: row?.reviewed_by,
      reviewedAt: row?.reviewed_at,
      approvedBy: row?.approved_by,
      approvedAt: row?.approved_at,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getReportById(id) {
    const { data, error } = await supabase?.from('compliance_reports')?.select('*')?.eq('id', id)?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      reportName: data?.report_name,
      reportPeriodType: data?.report_period_type,
      periodStart: data?.period_start,
      periodEnd: data?.period_end,
      reportStatus: data?.report_status,
      overallComplianceScore: data?.overall_compliance_score,
      previousPeriodScore: data?.previous_period_score,
      variancePercentage: data?.variance_percentage,
      varianceType: data?.variance_type,
      totalViolations: data?.total_violations,
      criticalViolations: data?.critical_violations,
      resolvedViolations: data?.resolved_violations,
      totalEmployees: data?.total_employees,
      compliantEmployees: data?.compliant_employees,
      reportSummary: data?.report_summary,
      generatedBy: data?.generated_by,
      generatedAt: data?.generated_at,
      reviewedBy: data?.reviewed_by,
      reviewedAt: data?.reviewed_at,
      approvedBy: data?.approved_by,
      approvedAt: data?.approved_at,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async getReportsByPeriodType(periodType) {
    const { data, error } = await supabase?.from('compliance_reports')?.select('*')?.eq('report_period_type', periodType)?.order('period_start', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      reportName: row?.report_name,
      periodStart: row?.period_start,
      periodEnd: row?.period_end,
      reportStatus: row?.report_status,
      overallComplianceScore: row?.overall_compliance_score,
      variancePercentage: row?.variance_percentage,
      varianceType: row?.variance_type
    })) || [];
  },

  async createReport(report) {
    const { data, error } = await supabase?.from('compliance_reports')?.insert({
        report_name: report?.reportName,
        report_period_type: report?.reportPeriodType,
        period_start: report?.periodStart,
        period_end: report?.periodEnd,
        report_status: report?.reportStatus || 'draft',
        overall_compliance_score: report?.overallComplianceScore,
        previous_period_score: report?.previousPeriodScore,
        total_employees: report?.totalEmployees,
        generated_by: report?.generatedBy
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      reportName: data?.report_name,
      reportStatus: data?.report_status,
      createdAt: data?.created_at
    };
  },

  async updateReportStatus(id, status, performedBy) {
    const { data, error } = await supabase?.rpc('update_report_status', {
      p_report_id: id,
      p_new_status: status,
      p_performed_by: performedBy
    });
    
    if (error) throw error;
    
    return data;
  },

  // ============================================================================
  // VARIANCE ANALYSIS
  // ============================================================================

  async getVarianceAnalysis(reportId) {
    const { data, error } = await supabase?.from('compliance_variance_analysis')?.select('*')?.eq('report_id', reportId)?.order('metric_category', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      reportId: row?.report_id,
      metricName: row?.metric_name,
      metricCategory: row?.metric_category,
      currentValue: row?.current_value,
      previousValue: row?.previous_value,
      varianceAmount: row?.variance_amount,
      variancePercentage: row?.variance_percentage,
      varianceType: row?.variance_type,
      department: row?.department,
      analysisNotes: row?.analysis_notes,
      createdAt: row?.created_at
    })) || [];
  },

  async createVarianceAnalysis(variance) {
    const { data, error } = await supabase?.from('compliance_variance_analysis')?.insert({
        report_id: variance?.reportId,
        metric_name: variance?.metricName,
        metric_category: variance?.metricCategory,
        current_value: variance?.currentValue,
        previous_value: variance?.previousValue,
        variance_amount: variance?.varianceAmount,
        variance_percentage: variance?.variancePercentage,
        variance_type: variance?.varianceType,
        department: variance?.department,
        analysis_notes: variance?.analysisNotes
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      metricName: data?.metric_name,
      varianceType: data?.variance_type
    };
  },

  // ============================================================================
  // AUDIT TRAIL
  // ============================================================================

  async getReportAuditTrail(reportId) {
    const { data, error } = await supabase?.from('report_audit_trail')?.select('*')?.eq('report_id', reportId)?.order('performed_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      reportId: row?.report_id,
      actionType: row?.action_type,
      actionDescription: row?.action_description,
      performedBy: row?.performed_by,
      performedAt: row?.performed_at,
      entityType: row?.entity_type,
      entityId: row?.entity_id,
      changesMade: row?.changes_made,
      metadata: row?.metadata,
      createdAt: row?.created_at
    })) || [];
  },

  async logReportAction(action) {
    const { data, error } = await supabase?.rpc('log_report_action', {
      p_report_id: action?.reportId,
      p_action_type: action?.actionType,
      p_action_description: action?.actionDescription,
      p_performed_by: action?.performedBy,
      p_entity_type: action?.entityType || null,
      p_entity_id: action?.entityId || null,
      p_changes: action?.changes || null
    });
    
    if (error) throw error;
    
    return data;
  },

  // ============================================================================
  // DLIR SUBMISSION PACKS
  // ============================================================================

  async getDlirSubmissionPacks(reportId) {
    const { data, error } = await supabase?.from('dlir_submission_packs')?.select('*')?.eq('report_id', reportId)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      reportId: row?.report_id,
      packName: row?.pack_name,
      submissionStatus: row?.submission_status,
      submissionPeriod: row?.submission_period,
      employeeRosterUrl: row?.employee_roster_url,
      payrollSummaryUrl: row?.payroll_summary_url,
      filingConfirmationsUrl: row?.filing_confirmations_url,
      violationsReportUrl: row?.violations_report_url,
      regulatoryCitationsUrl: row?.regulatory_citations_url,
      completePackUrl: row?.complete_pack_url,
      dlirReferenceNumber: row?.dlir_reference_number,
      submittedToDlirAt: row?.submitted_to_dlir_at,
      acknowledgedByDlirAt: row?.acknowledged_by_dlir_at,
      acknowledgementReference: row?.acknowledgement_reference,
      rejectionReason: row?.rejection_reason,
      preparedBy: row?.prepared_by,
      preparedAt: row?.prepared_at,
      metadata: row?.metadata,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async createDlirSubmissionPack(pack) {
    const { data, error } = await supabase?.from('dlir_submission_packs')?.insert({
        report_id: pack?.reportId,
        pack_name: pack?.packName,
        submission_status: pack?.submissionStatus || 'pending',
        submission_period: pack?.submissionPeriod,
        prepared_by: pack?.preparedBy,
        metadata: pack?.metadata
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      packName: data?.pack_name,
      submissionStatus: data?.submission_status,
      createdAt: data?.created_at
    };
  },

  async updateDlirSubmissionStatus(id, status, updates = {}) {
    const updateData = {
      submission_status: status,
      updated_at: new Date()?.toISOString()
    };

    if (status === 'submitted' && updates?.dlirReferenceNumber) {
      updateData.submitted_to_dlir_at = new Date()?.toISOString();
      updateData.dlir_reference_number = updates?.dlirReferenceNumber;
    }

    if (status === 'acknowledged') {
      updateData.acknowledged_by_dlir_at = new Date()?.toISOString();
      if (updates?.acknowledgementReference) {
        updateData.acknowledgement_reference = updates?.acknowledgementReference;
      }
    }

    if (status === 'rejected' && updates?.rejectionReason) {
      updateData.rejection_reason = updates?.rejectionReason;
    }

    const { data, error } = await supabase?.from('dlir_submission_packs')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      submissionStatus: data?.submission_status,
      updatedAt: data?.updated_at
    };
  },

  // ============================================================================
  // DEPARTMENT BREAKDOWN
  // ============================================================================

  async getDepartmentBreakdown(reportId) {
    const { data, error } = await supabase?.from('report_department_breakdown')?.select('*')?.eq('report_id', reportId)?.order('compliance_score', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      reportId: row?.report_id,
      department: row?.department,
      complianceScore: row?.compliance_score,
      previousScore: row?.previous_score,
      variancePercentage: row?.variance_percentage,
      totalEmployees: row?.total_employees,
      violationsCount: row?.violations_count,
      criticalViolations: row?.critical_violations,
      payrollAccuracy: row?.payroll_accuracy,
      leaveCompliance: row?.leave_compliance,
      contractCompliance: row?.contract_compliance,
      createdAt: row?.created_at
    })) || [];
  },

  // ============================================================================
  // KEY FINDINGS
  // ============================================================================

  async getKeyFindings(reportId) {
    const { data, error } = await supabase?.from('report_key_findings')?.select('*')?.eq('report_id', reportId)?.order('severity', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      reportId: row?.report_id,
      findingType: row?.finding_type,
      severity: row?.severity,
      title: row?.title,
      description: row?.description,
      affectedDepartments: row?.affected_departments,
      affectedEmployeeCount: row?.affected_employee_count,
      recommendation: row?.recommendation,
      actionRequired: row?.action_required,
      deadline: row?.deadline,
      createdAt: row?.created_at
    })) || [];
  }
};

export default complianceReportService;